import React, { useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Banknote, AlertTriangle, ShieldCheck } from "lucide-react";

import { getBackendBase } from "@/lib/api";
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

/** ABA routing-number checksum (matches backend services/encryption.py). */
export function isValidAba(routing) {
    if (!routing || routing.length !== 9 || !/^\d{9}$/.test(routing)) return false;
    const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(routing[i], 10) * w[i];
    return sum % 10 === 0;
}

/**
 * Phase F2 — Bank-information capture step for the ACH leg of a bundled signing
 * session. Shown after the rental contract is signed and before the ACH
 * signature pad. Posts to /sign/contract/{token}/ach-bank-info which encrypts
 * the routing/account numbers at rest.
 *
 * Props:
 *   token, achDoc, defaultHolderName, onBack, onCaptured(updatedAchDoc)
 */
export function AchBankInfoStep({ token, achDoc, defaultHolderName = "", onBack, onCaptured }) {
    const [bankName, setBankName] = useState("");
    const [holderName, setHolderName] = useState(defaultHolderName);
    const [accountType, setAccountType] = useState("checking");
    const [routing, setRouting] = useState("");
    const [account, setAccount] = useState("");
    const [accountConfirm, setAccountConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const abaOk = useMemo(() => isValidAba(routing), [routing]);
    const accountsMatch = account.length > 0 && account === accountConfirm;
    const ready = bankName.trim() && holderName.trim() && abaOk && accountsMatch && /^\d{4,}$/.test(account);

    const submit = async () => {
        setErr(null); setBusy(true);
        try {
            const { data } = await axios.post(
                `${BACKEND_URL}/api/sign/contract/${token}/ach-bank-info`,
                {
                    bank_name: bankName.trim(),
                    account_holder_name: holderName.trim(),
                    account_type: accountType,
                    routing_number: routing,
                    account_number: account,
                    account_number_confirm: accountConfirm,
                },
                { params: { doc_id: achDoc?.doc_id } },
            );
            onCaptured?.(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not save bank information");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-5" data-testid="sign-bank-info-step">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-1 flex items-center gap-1.5">
                        <Banknote size={12} /> ACH AUTHORIZATION · BANK INFO
                    </p>
                    <h2 className="font-display text-2xl font-bold text-slate-900">Tell us where to debit</h2>
                    <p className="text-sm text-slate-600 mt-1">
                        These numbers are encrypted before they leave your browser&apos;s session and are
                        never visible to anyone outside your account team.
                    </p>
                </div>

                {err && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2"
                        data-testid="bank-info-error">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <span>{err}</span>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5 col-span-2">
                        <Label htmlFor="bank-name">Bank name *</Label>
                        <Input id="bank-name" value={bankName} onChange={(e) => setBankName(e.target.value)}
                            placeholder="e.g. Chase, Wells Fargo" data-testid="bank-name" />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                        <Label htmlFor="bank-holder">Account holder name *</Label>
                        <Input id="bank-holder" value={holderName} onChange={(e) => setHolderName(e.target.value)}
                            placeholder="As it appears on your bank statement" data-testid="bank-holder" />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                        <Label>Account type *</Label>
                        <div className="flex items-center gap-3">
                            {["checking", "savings"].map((t) => (
                                <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="account-type" value={t}
                                        checked={accountType === t} onChange={() => setAccountType(t)}
                                        data-testid={`bank-account-type-${t}`} />
                                    <span className="capitalize">{t}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-1.5 col-span-2">
                        <Label htmlFor="bank-routing">Routing number * (9 digits)</Label>
                        <Input id="bank-routing"
                            value={routing}
                            onChange={(e) => setRouting(e.target.value.replace(/\D/g, "").slice(0, 9))}
                            inputMode="numeric"
                            data-testid="bank-routing"
                            data-aba-valid={abaOk ? "true" : "false"}
                            className={routing.length === 9 && !abaOk ? "border-red-400 focus-visible:ring-red-300" : ""}
                            placeholder="e.g. 111000025" />
                        {routing.length === 9 && !abaOk && (
                            <p className="text-xs text-red-700" data-testid="bank-routing-error">
                                That routing number didn&apos;t pass the ABA checksum. Double-check the number on your check or bank statement.
                            </p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="bank-account">Account number *</Label>
                        <Input id="bank-account" value={account}
                            onChange={(e) => setAccount(e.target.value.replace(/\D/g, ""))}
                            inputMode="numeric" data-testid="bank-account" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="bank-account-confirm">Confirm account number *</Label>
                        <Input id="bank-account-confirm" value={accountConfirm}
                            onChange={(e) => setAccountConfirm(e.target.value.replace(/\D/g, ""))}
                            inputMode="numeric"
                            data-testid="bank-account-confirm"
                            className={accountConfirm.length > 0 && !accountsMatch ? "border-red-400 focus-visible:ring-red-300" : ""} />
                        {accountConfirm.length > 0 && !accountsMatch && (
                            <p className="text-xs text-red-700" data-testid="bank-account-mismatch">Account numbers don&apos;t match.</p>
                        )}
                    </div>
                </div>

                <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-start gap-2 text-xs text-emerald-900">
                    <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                    <span>Numbers are encrypted with AES-256 before storage. Only authorized teammates can decrypt them, and every reveal is logged in the audit trail.</span>
                </div>

                <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                    <Button variant="outline" onClick={onBack} data-testid="bank-info-back">← Back</Button>
                    <Button onClick={submit} disabled={!ready || busy}
                        className="bg-blue-900 hover:bg-blue-950" data-testid="bank-info-continue">
                        {busy ? "Saving…" : "Continue to ACH signature →"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

export default AchBankInfoStep;
